/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印项编辑状态相关 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-09 15:00:00
 */

import { useCallback, useMemo } from 'react'
import { WatermarkItem } from '../context/WatermarkBatchContext'

/**
 * 字段禁用状态
 */
export interface FieldDisabledStatus {
  switch: boolean
  title: boolean
  content: boolean
}

/**
 * 根据 edit_type 判断字段是否应该禁用
 * @param editType 编辑类型
 * @returns 字段禁用状态
 */
export const getFieldDisabledStatus = (
  editType: number | undefined
): FieldDisabledStatus => {
  // edit_type: 0, 1, 4, 其他 -> switch、title、content 都禁用
  // edit_type: 2 -> switch 支持，title 禁用，content 支持
  // edit_type: 3, 5 -> switch、title、content 都支持
  if (editType === undefined || editType === null) {
    return { switch: false, title: false, content: false }
  }

  // 确保 editType 是数字类型
  const editTypeNum =
    typeof editType === 'string' ? parseInt(editType, 10) : editType
  if (isNaN(editTypeNum)) {
    return { switch: true, title: true, content: true }
  }

  switch (editTypeNum) {
    case 0:
    case 1:
    case 4:
      return { switch: true, title: true, content: true }
    case 2:
      return { switch: false, title: true, content: false }
    case 3:
    case 5:
      return { switch: false, title: false, content: false }
    default:
      return { switch: true, title: true, content: true }
  }
}

/**
 * 判断水印项是否可编辑（至少有一个字段可编辑）
 * @param item 水印项
 * @returns 是否可编辑
 */
export const isWatermarkItemEditable = (item: WatermarkItem): boolean => {
  const disabledStatus = getFieldDisabledStatus(item.edit_type)
  // 如果所有字段都被禁用，则不可编辑
  return !(
    disabledStatus.switch &&
    disabledStatus.title &&
    disabledStatus.content
  )
}

/**
 * 计算可编辑项的数量
 * @param watermarkData 水印数据数组
 * @returns 可编辑项的数量
 */
export const calculateEditableItemCount = (
  watermarkData: WatermarkItem[] | undefined
): number => {
  if (!watermarkData || watermarkData.length === 0) {
    return 0
  }

  return watermarkData.filter(isWatermarkItemEditable).length
}

/**
 * 判断是否所有项都不支持编辑
 * @param watermarkData 水印数据数组
 * @returns 是否所有项都不可编辑
 */
export const isAllItemsDisabled = (watermarkData: WatermarkItem[]): boolean => {
  if (watermarkData.length === 0) {
    return false
  }

  return watermarkData.every((item) => {
    const disabledStatus = getFieldDisabledStatus(item.edit_type)
    return (
      disabledStatus.switch && disabledStatus.title && disabledStatus.content
    )
  })
}

/**
 * 水印项编辑状态相关 Hook
 */
export const useWatermarkItemEditStatus = () => {
  return {
    getFieldDisabledStatus,
    isWatermarkItemEditable,
    calculateEditableItemCount,
    isAllItemsDisabled
  }
}

export default useWatermarkItemEditStatus
